// ---------------------------------------------------------------------------
// DocumentationV3 — pagina principal da view "Documentation V3" (Variante B).
//
// Composicao:
//   - Card framing canonico do app (CardHeader com icone + titulo + busca
//     no canto direito, mesma altura).
//   - Card ocupa altura fixa (calc(100vh - 130px)); rail e area de
//     conteudo rolam internamente (nao a pagina).
//   - Shell: TopicsRail (esquerda, 330px) + SubtopicsScroll (direita,
//     fundo cinza claro, scroll-spy ativo).
//
// Estilo da busca: espelho 1:1 do filtro por nome em InputsV3.js
// (standard variant, disableUnderline, fundo cinza claro arredondado,
// fonte 0.85rem) — sem icone de busca por decisao explicita.
//
// Estado proprio:
//   - currentTopicSlug, scrolledSubSlug, query.
//   - Sincronizacao bidirecional com URL via useSearchParams.
//
// Fluxos:
//   - Click em Topic da rail -> troca currentTopicSlug + reseta scroll
//     do container para o topo.
//   - Click em SubTopic do mesmo Topic -> scrollTo (smooth).
//   - Click em SubTopic de outro Topic -> troca Topic + scrollTo (instant).
//   - Scroll do container de conteudo -> scroll-spy atualiza
//     scrolledSubSlug -> URL.
//
// Wave 1b: data vem do hook useDocumentationV3 (mock). Wave 2: hook
// chama API real.
// ---------------------------------------------------------------------------

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import cx from "classnames";
import {
  Close,
  Description,
  Home,
  ChevronLeft,
  ChevronRight,
} from "@material-ui/icons";
import { ThreeDots } from "react-loader-spinner";

import Card from "../../components/Card/Card.js";
import CardIcon from "../../components/Card/CardIcon.js";
import CardHeader from "../../components/Card/CardHeader.js";
import useThemeColors from "../../hooks/useThemeColors";
import AuthContext from "../../contexts/AuthProvider";

import documentationV3Style from "../../assets/jss/material-dashboard-pro-react/views/documentationV3Style";
import {
  TopicsRail,
  SubtopicsScroll,
  useDocumentationV3,
  RegenPreviewModal,
  WelcomeScreen,
} from "./documentation_v3_components";

const useStyles = makeStyles(documentationV3Style);

export default function DocumentationV3() {
  const classes = useStyles();
  const { t } = useTranslation();
  const { clientColor } = useThemeColors();
  const { userDetails } = useContext(AuthContext);
  // Indicadores de "desatualizado" e botoes de regen ficam visiveis
  // apenas para editores (DEV+IMG) — alinhado com a permission
  // ``regen_docs`` no backend (que tambem rejeita roles fora desse
  // conjunto, mesmo se a UI for forjada). Usuarios consultores/clientes
  // veem apenas o conteudo final + "Atualizado em DD/MMM/YYYY".
  const isEditor = ["DEV", "IMG"].includes(userDetails?.user_role);

  // Hook conversa com a API real (`api/documentation-v3/topics/`). O
  // app vem da current_implementation do usuario (backend resolve).
  const {
    data,
    loading,
    error,
    regenSubtopic,
    acceptRegen,
    confirmCurrent,
    regenInflight,
  } = useDocumentationV3();

  const [searchParams, setSearchParams] = useSearchParams();
  const [currentTopicSlug, setCurrentTopicSlug] = useState(null);
  const [scrolledSubSlug, setScrolledSubSlug] = useState(null);
  const [query, setQuery] = useState("");
  // Tela inicial (boas-vindas): default ao abrir, exceto quando a URL ja
  // aponta para um topico/subtopico especifico (deep link).
  const [showWelcome, setShowWelcome] = useState(true);
  // Recolher/expandir a rail de topicos. Default aberto; toggle simples.
  const [railCollapsed, setRailCollapsed] = useState(false);
  // Guard de boot: o effect inicial roda uma unica vez apos `data` carregar.
  const bootedRef = useRef(false);
  // Regen preview modal — null = fechado; objeto = candidato em revisao.
  // Shape: {subtopicId, subtopicTitle, candidateHtml, currentHtml,
  //         currentSignature, trace, gabarito}.
  const [regenPreview, setRegenPreview] = useState(null);
  const [regenError, setRegenError] = useState(null);

  const scrollRef = useRef(null);

  // ----- Boot: aplica deep link da URL assim que data carrega -----
  useEffect(() => {
    if (!data || bootedRef.current) return;
    bootedRef.current = true;

    const urlTopic = searchParams.get("topic");
    // Pre-seleciona o primeiro topico em estado (para que a rail tenha
    // um alvo quando o usuario sair da tela inicial), mas so abre o
    // conteudo se a URL pedir explicitamente um topico (deep link).
    const initialTopic = urlTopic || data.topics[0]?.slug || null;
    setCurrentTopicSlug(initialTopic);
    const topic = data.topics.find((tt) => tt.slug === initialTopic);
    const initialSub =
      searchParams.get("subtopic") || topic?.subtopics[0]?.slug || null;
    setScrolledSubSlug(initialSub);

    if (urlTopic) {
      setShowWelcome(false);
      if (searchParams.get("subtopic")) {
        setTimeout(() => {
          scrollRef.current?.scrollToSub(initialSub, "instant");
        }, 50);
      }
    } else {
      setShowWelcome(true);
    }
    // Intencionalmente so depende de `data`: queremos rodar apenas no
    // primeiro paint apos a data carregar, nao em cada mudanca de
    // searchParams ou estado interno.
  }, [data]);

  // ----- Sincroniza estado -> URL (replace, nao push) -----
  useEffect(() => {
    if (!bootedRef.current) return;
    const next = new URLSearchParams(searchParams);
    if (showWelcome || !currentTopicSlug) {
      // Tela inicial nao deve carregar topico/subtopico na URL.
      next.delete("topic");
      next.delete("subtopic");
    } else {
      next.set("topic", currentTopicSlug);
      if (scrolledSubSlug) next.set("subtopic", scrolledSubSlug);
      else next.delete("subtopic");
    }
    setSearchParams(next, { replace: true });
    // Intencionalmente nao deps do `searchParams`/`setSearchParams`:
    // queremos espelhar mudancas de estado interno para URL, nao o
    // contrario (URL -> estado roda no useEffect de boot acima).
  }, [showWelcome, currentTopicSlug, scrolledSubSlug]);

  // Buscar a partir da tela inicial deve revelar o conteudo (caso
  // contrario, a busca filtraria a rail sem nada aparecer a direita).
  useEffect(() => {
    if (query.trim() && showWelcome) setShowWelcome(false);
  }, [query, showWelcome]);

  const currentTopic = useMemo(
    () => data?.topics.find((tt) => tt.slug === currentTopicSlug) || null,
    [data, currentTopicSlug],
  );

  // ----- Handlers -----
  const handleSelectTopic = useCallback(
    (slug) => {
      setShowWelcome(false);
      setCurrentTopicSlug(slug);
      const topic = data?.topics.find((tt) => tt.slug === slug);
      setScrolledSubSlug(topic?.subtopics[0]?.slug || null);
    },
    [data],
  );

  const handleSelectSub = useCallback((subSlug) => {
    setShowWelcome(false);
    setScrolledSubSlug(subSlug);
    scrollRef.current?.scrollToSub(subSlug, "smooth");
  }, []);

  const handleSelectTopicAndSub = useCallback((topicSlug, subSlug) => {
    setShowWelcome(false);
    setCurrentTopicSlug(topicSlug);
    setScrolledSubSlug(subSlug);
    setTimeout(() => {
      scrollRef.current?.scrollToSub(subSlug, "instant");
    }, 30);
  }, []);

  // Volta para a tela inicial de boas-vindas.
  const handleGoHome = useCallback(() => {
    setShowWelcome(true);
  }, []);

  const handleSubInView = useCallback((subSlug) => {
    setScrolledSubSlug((prev) => (prev === subSlug ? prev : subSlug));
  }, []);

  // ----- Regen handlers -----
  // Fluxo dividido em duas etapas para permitir instrucoes previas:
  //   1. ``handleOpenRegen`` abre o modal SEM gerar — usuario digita
  //      instrucoes opcionais ("faca do zero", "adicione X").
  //   2. ``handleGenerate`` dispara a API com as instrucoes; resposta
  //      preenche ``candidateHtml`` + ``trace`` no preview.
  //   3. Usuario aceita ou descarta. Pode tambem gerar de novo com
  //      instrucoes diferentes sem fechar o modal.
  const handleOpenRegen = useCallback(
    (subtopicId, subtopicTitle, currentHtml) => {
      setRegenError(null);
      setRegenPreview({
        subtopicId,
        subtopicTitle,
        currentHtml: currentHtml || "",
        candidateHtml: "",
        currentSignature: {},
        trace: null,
        gabarito: null,
        promptName: null,
      });
    },
    [],
  );

  const handleGenerate = useCallback(
    async (extraInstructions, baseHtml) => {
      if (!regenPreview) return;
      setRegenError(null);
      try {
        const candidate = await regenSubtopic(regenPreview.subtopicId, {
          extraInstructions,
          // Modal passa o HTML da versao SELECIONADA no carrossel
          // (atual ou v1, v2, ... gerada na sessao). Quando ausente
          // (1a geracao), o backend cai em regra normal.
          baseHtml: baseHtml || "",
        });
        setRegenPreview((prev) =>
          prev
            ? {
                ...prev,
                candidateHtml: candidate.candidate_html || "",
                currentHtml: candidate.current_html || prev.currentHtml,
                currentSignature: candidate.current_signature || {},
                trace: candidate.trace || null,
                gabarito: candidate.gabarito || null,
                promptName: candidate.prompt_name || null,
              }
            : prev,
        );
      } catch (e) {
        const msg =
          e?.response?.data?.error_message ||
          e?.response?.data?.error ||
          e?.message ||
          "regen_failed";
        setRegenError(msg);
      }
    },
    [regenPreview, regenSubtopic],
  );

  // O modal envia o HTML da versao SELECIONADA (pode nao ser a mais
  // recente, ja' que o usuario pode navegar entre vN no carrossel).
  // Mantemos fallback para `regenPreview.candidateHtml` por seguranca
  // (caso uma chamada antiga ainda passe so notes/sem html).
  const handleAccept = useCallback(
    async (html, notes) => {
      if (!regenPreview) return;
      const effectiveHtml =
        typeof html === "string" && html.length
          ? html
          : regenPreview.candidateHtml;
      const effectiveNotes = typeof html === "string" ? notes : html;
      try {
        await acceptRegen(
          regenPreview.subtopicId,
          effectiveHtml,
          regenPreview.currentSignature,
          effectiveNotes,
        );
        setRegenPreview(null);
      } catch (e) {
        setRegenError(
          e?.response?.data?.error_message || e?.message || "accept_failed",
        );
      }
    },
    [regenPreview, acceptRegen],
  );

  const handleConfirmCurrent = useCallback(async () => {
    if (!regenPreview) return;
    try {
      await confirmCurrent(regenPreview.subtopicId);
      setRegenPreview(null);
    } catch (e) {
      setRegenError(
        e?.response?.data?.error_message || e?.message || "confirm_failed",
      );
    }
  }, [regenPreview, confirmCurrent]);

  const handleDiscard = useCallback(() => {
    setRegenPreview(null);
    setRegenError(null);
  }, []);

  const renderLoading = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        height: "100%",
        padding: "40px",
      }}
    >
      <ThreeDots
        width="60px"
        height="60px"
        color={clientColor}
        ariaLabel="loading"
      />
    </div>
  );

  return (
    <div className={classes.pageContainer}>
      {/* Container 1 — header (icone + titulo + busca) */}
      <Card className={classes.topCard}>
        <CardHeader
          color="client"
          icon
          className={classes.cardHeaderRel}
          style={{ minHeight: "54px" }}
        >
          <CardIcon color="client">
            <Description />
          </CardIcon>
          <h4 className={classes.cardIconTitle}>
            {t("modeling_documentation.documentation")}
          </h4>
          {/* Honeypot anti-autofill: espelha o fix de InputsV3.js.
              Browsers (Chrome/Edge) e password managers (1Password,
              LastPass) preferem o primeiro campo de texto do form para
              autofill — mantendo um "fake" off-screen, o nosso campo
              real (busca) fica intocado. */}
          <input
            type="text"
            name="username"
            autoComplete="username"
            tabIndex={-1}
            aria-hidden="true"
            style={{
              position: "absolute",
              left: -10000,
              top: -10000,
              width: 1,
              height: 1,
              opacity: 0,
            }}
            readOnly
          />
          <TextField
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t(
              "modeling_documentation.search_placeholder",
              "Buscar topicos e formulas...",
            )}
            variant="standard"
            size="small"
            fullWidth
            autoComplete="off"
            // ``name`` aleatorio evita que o navegador casee este input
            // com cache de autofill de execucoes anteriores.
            name={`docs-v3-filter-${Math.random().toString(36).slice(2, 8)}`}
            onFocus={(e) => e.target.removeAttribute("readonly")}
            className={classes.searchField}
            inputProps={{
              // Camada extra: campo entra como ``readOnly`` no mount e
              // libera no primeiro focus. Chrome ignora autofill em
              // campos readOnly. Combinado com o honeypot e os
              // ``data-*-ignore``, cobre 1Password/LastPass/Chrome.
              readOnly: true,
              autoComplete: "new-password",
              "data-lpignore": "true",
              "data-form-type": "other",
              "data-1p-ignore": "true",
              role: "presentation",
              spellCheck: "false",
              autoCorrect: "off",
              autoCapitalize: "off",
              style: { padding: 0, height: 24, lineHeight: "24px" },
            }}
            InputProps={{
              disableUnderline: true,
              style: {
                background: "#f1f3f4",
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                padding: "4px 10px",
                minHeight: 32,
                fontSize: "0.85rem",
              },
              endAdornment: query ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label={t("modeling_documentation.clear_search")}
                    onClick={() => setQuery("")}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </CardHeader>
      </Card>

      {/* Containers 2 + 3 — rail (esquerda) + conteudo (direita) */}
      <div className={classes.bottomRow}>
        <Card
          className={cx(classes.railCard, {
            [classes.railCardCollapsed]: railCollapsed,
          })}
        >
          <div
            className={cx(classes.railHeader, {
              [classes.railHeaderCollapsed]: railCollapsed,
            })}
          >
            {!railCollapsed && (
              <Button
                size="small"
                className={cx(classes.railHomeBtn, {
                  [classes.railHomeBtnActive]: showWelcome,
                })}
                startIcon={<Home fontSize="small" />}
                onClick={handleGoHome}
              >
                {t("modeling_documentation.rail_home")}
              </Button>
            )}
            <Tooltip
              title={
                railCollapsed
                  ? t("modeling_documentation.rail_expand")
                  : t("modeling_documentation.rail_collapse")
              }
            >
              <IconButton
                size="small"
                className={classes.railToggleBtn}
                aria-label={
                  railCollapsed
                    ? t("modeling_documentation.rail_expand")
                    : t("modeling_documentation.rail_collapse")
                }
                onClick={() => setRailCollapsed((v) => !v)}
              >
                {railCollapsed ? <ChevronRight /> : <ChevronLeft />}
              </IconButton>
            </Tooltip>
          </div>
          {!railCollapsed &&
            (loading || !data ? (
              renderLoading()
            ) : (
              <TopicsRail
                topics={data.topics}
                currentTopicSlug={showWelcome ? null : currentTopicSlug}
                scrolledSubSlug={showWelcome ? null : scrolledSubSlug}
                query={query}
                onSelectTopic={handleSelectTopic}
                onSelectSub={handleSelectSub}
                onSelectTopicAndSub={handleSelectTopicAndSub}
                showDrift={isEditor}
              />
            ))}
        </Card>
        <Card className={classes.contentCard}>
          {loading || !data ? (
            renderLoading()
          ) : showWelcome ? (
            <WelcomeScreen />
          ) : (
            <SubtopicsScroll
              ref={scrollRef}
              topic={currentTopic}
              query={query}
              onSubInView={handleSubInView}
              onRegenSubtopic={isEditor ? handleOpenRegen : undefined}
              regenInflight={regenInflight}
              canRegen={isEditor}
            />
          )}
        </Card>
      </div>

      <RegenPreviewModal
        open={!!regenPreview}
        preview={regenPreview}
        error={regenError}
        generating={
          regenPreview ? !!regenInflight?.[regenPreview.subtopicId] : false
        }
        onClearError={() => setRegenError(null)}
        onGenerate={handleGenerate}
        onAccept={handleAccept}
        onDiscard={handleDiscard}
        onConfirmCurrent={handleConfirmCurrent}
      />

      {error && !regenPreview ? (
        <div
          style={{
            position: "fixed",
            bottom: 16,
            right: 16,
            background: "#b71c1c",
            color: "#fff",
            padding: "10px 14px",
            borderRadius: 6,
            fontSize: 12,
            zIndex: 1300,
          }}
        >
          {t("modeling_documentation.loading_error", { message: error })}
        </div>
      ) : null}
    </div>
  );
}
